import React, { useState, useEffect, useRef, useCallback } from "react";
import "./shippingForm.css";

// ─── TypeaheadSelect Component ─────────────────────────────────────────────────

const TypeaheadSelect = ({ options, value, onChange, placeholder, label, icon, locked }) => {
  const [inputVal, setInputVal]       = useState("");
  const [focused, setFocused]         = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const [listPos, setListPos]         = useState({ top: 0, left: 0, width: 0 });
  const inputRef = useRef(null);
  const listRef  = useRef(null);

  // Sync display when external value changes (e.g. ZIP auto-fill)
  useEffect(() => {
    if (value) {
      const match = options.find((o) => o.code === value);
      if (match) setInputVal(match.name);
    } else {
      setInputVal("");
    }
  }, [value, options]);

  // Filtered suggestions — starts-with ranked first, max 5
  const suggestions = React.useMemo(() => {
    if (!focused || locked) return [];
    const q = inputVal.trim().toLowerCase();
    if (!q) return [];
    const exact = options.find((o) => o.name.toLowerCase() === q || o.code.toLowerCase() === q);
    if (exact) return [];
    const sw = options.filter((o) => o.name.toLowerCase().startsWith(q) || o.code.toLowerCase() === q);
    const ct = options.filter(
      (o) => !sw.includes(o) && (o.name.toLowerCase().includes(q) || o.code.toLowerCase().includes(q))
    );
    return [...sw, ...ct].slice(0, 5);
  }, [inputVal, focused, options, locked]);

  // Ghost suffix — inline completion hint
  const topSuggestion = suggestions[0];
  const ghostSuffix =
    topSuggestion &&
    inputVal.length > 0 &&
    topSuggestion.name.toLowerCase().startsWith(inputVal.toLowerCase())
      ? topSuggestion.name.slice(inputVal.length)
      : "";

  // Keep floating list anchored under the input
  const updateListPos = useCallback(() => {
    if (!inputRef.current) return;
    const r = inputRef.current.getBoundingClientRect();
    setListPos({ top: r.bottom + 4, left: r.left, width: r.width });
  }, []);

  useEffect(() => {
    if (focused && suggestions.length > 0) {
      updateListPos();
      window.addEventListener("scroll", updateListPos, true);
      window.addEventListener("resize", updateListPos);
      return () => {
        window.removeEventListener("scroll", updateListPos, true);
        window.removeEventListener("resize", updateListPos);
      };
    }
  }, [focused, suggestions.length, updateListPos]);

  const selectOption = (opt) => {
    setInputVal(opt.name);
    onChange(opt.code);
    setFocused(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === "Tab" && ghostSuffix) {
      e.preventDefault();
      if (suggestions[0]) selectOption(suggestions[0]);
    } else if (e.key === "Enter") {
      const pick = suggestions[highlighted] || suggestions[0];
      if (pick) { e.preventDefault(); selectOption(pick); }
    } else if (e.key === "Escape") {
      setFocused(false);
    }
  };

  const handleChange = (e) => {
    const v = e.target.value;
    setInputVal(v);
    setHighlighted(0);
    if (!v) { onChange(""); return; }
    const exact = options.find(
      (o) => o.name.toLowerCase() === v.toLowerCase() || o.code.toLowerCase() === v.toLowerCase()
    );
    if (exact) onChange(exact.code);
  };

  const handleBlur = () => {
    setTimeout(() => {
      if (listRef.current && listRef.current.contains(document.activeElement)) return;
      setFocused(false);
      const match = options.find((o) => o.name.toLowerCase() === inputVal.trim().toLowerCase());
      if (match) {
        setInputVal(match.name);
        onChange(match.code);
      } else if (!value) {
        setInputVal("");
      } else {
        const cur = options.find((o) => o.code === value);
        if (cur) setInputVal(cur.name);
      }
    }, 160);
  };

  const showList = focused && suggestions.length > 0;

  return (
    <div className="ta-wrapper">
      {label && <span className="sf-field-label">{label}</span>}
      <div className="ta-input-wrap">
        {ghostSuffix && focused && (
          <div className="ta-ghost" aria-hidden="true">
            <span style={{ opacity: 0 }}>{inputVal}</span>
            <span className="ta-ghost-suffix">{ghostSuffix}</span>
          </div>
        )}
        {icon && <span className="ta-icon">{icon}</span>}
        <input
          ref={inputRef}
          className={`sf-input ta-input${icon ? " ta-input--icon" : ""}${value ? " ta-input--selected" : ""}`}
          type="text"
          value={inputVal}
          placeholder={locked ? "" : placeholder}
          onChange={handleChange}
          onFocus={() => { setFocused(true); setHighlighted(0); updateListPos(); }}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          readOnly={locked}
        />
        {value && <span className="ta-check">✓</span>}
      </div>

      {showList && (
        <ul
          ref={listRef}
          className="ta-list"
          style={{ position: "fixed", top: listPos.top, left: listPos.left, width: listPos.width, zIndex: 9999 }}
          onMouseDown={(e) => e.preventDefault()}
        >
          {suggestions.map((opt, i) => {
            const q = inputVal.trim().toLowerCase();
            const nameLC = opt.name.toLowerCase();
            const idx = nameLC.indexOf(q);
            return (
              <li
                key={opt.code}
                className={`ta-item${i === highlighted ? " ta-item--active" : ""}`}
                onMouseEnter={() => setHighlighted(i)}
                onClick={() => selectOption(opt)}
              >
                {opt.flag && <span className="ta-item-flag">{opt.flag}</span>}
                <span className="ta-item-name">
                  {idx >= 0 ? (
                    <>
                      {opt.name.slice(0, idx)}
                      <strong>{opt.name.slice(idx, idx + q.length)}</strong>
                      {opt.name.slice(idx + q.length)}
                    </>
                  ) : opt.name}
                </span>
                <span className="ta-item-code">{opt.code}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

// Aliases for backward compatibility
export { TypeaheadSelect };
export const CustomSelect = TypeaheadSelect;
export default TypeaheadSelect;